// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 🏋️ TRAINING PAGE
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExerciseLibrary } from '../components/training/ExerciseLibrary';
import { ExerciseEditor } from '../components/training/ExerciseEditor';
import { ExerciseSession } from '../components/training/ExerciseSession';
import { ExerciseSessionHistory } from '../components/training/ExerciseSessionHistory';
import { VirtualTrainingEditor } from '../components/training/VirtualTrainingEditor';
import { VirtualTrainingSession } from '../components/training/VirtualTrainingSession';
import { VirtualTrainingHistory } from '../components/training/VirtualTrainingHistory';
import type { Exercise, VirtualTrainingWithExercises } from '../types';
import './Training.css';

type ViewType = 'exercises' | 'trainings' | 'editor' | 'session' | 'history' | 'vt-editor' | 'vt-session' | 'vt-history';

const Training: React.FC = () => {
  const { t } = useTranslation();
  const [view, setView] = useState<ViewType>('exercises');
  const [editingExercise, setEditingExercise] = useState<Exercise | undefined>();
  const [activeExercise, setActiveExercise] = useState<Exercise | undefined>();
  const [editingTraining, setEditingTraining] = useState<VirtualTrainingWithExercises | undefined>();
  const [activeTraining, setActiveTraining] = useState<VirtualTrainingWithExercises | undefined>();
  const [refreshKey, setRefreshKey] = useState(0);

  const handleCreateExercise = () => {
    setEditingExercise(undefined);
    setView('editor');
  };

  const handleEditExercise = (exercise: Exercise) => {
    setEditingExercise(exercise);
    setView('editor');
  };

  const handleDeleteExercise = async (exerciseId: number) => {
    try {
      const { deleteExercise } = await import('../services/exercises');
      await deleteExercise(exerciseId);
      setView('exercises');
      setEditingExercise(undefined);
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error('Failed to delete exercise:', err);
      alert('Löschen fehlgeschlagen');
    }
  };

  const handleStartSession = (exercise: Exercise) => {
    setActiveExercise(exercise);
    setView('session');
  };

  const handleSaveExercise = () => {
    setView('exercises');
    setEditingExercise(undefined);
    setRefreshKey(prev => prev + 1);
  };

  const handleSessionComplete = () => {
    setView('exercises');
    setActiveExercise(undefined);
    setRefreshKey(prev => prev + 1);
  };

  const handleCancel = () => {
    setView('exercises');
    setEditingExercise(undefined);
    setActiveExercise(undefined);
  };

  // Virtual Training Handlers
  const handleCreateTraining = () => {
    setEditingTraining(undefined);
    setView('vt-editor');
  };

  const handleSaveTraining = () => {
    setView('trainings');
    setEditingTraining(undefined);
    setRefreshKey(prev => prev + 1);
  };

  const handleDeleteTraining = async (trainingId: number) => {
    try {
      const { deleteVirtualTraining } = await import('../services/virtualTrainings');
      await deleteVirtualTraining(trainingId);
      setView('trainings');
      setEditingTraining(undefined);
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error('Failed to delete training:', err);
      alert('Löschen fehlgeschlagen');
    }
  };

  const handleStartTraining = (training: VirtualTrainingWithExercises) => {
    setActiveTraining(training);
    setView('vt-session');
  };

  const handleTrainingComplete = () => {
    setView('trainings');
    setActiveTraining(undefined);
    setRefreshKey(prev => prev + 1);
  };

  const handleTrainingCancel = () => {
    setView('trainings');
    setActiveTraining(undefined);
  };

  return (
    <div className="training-page">
      <div className="training-header">
        <h1>{t('training.title')}</h1>
        <p>{t('training.subtitle')}</p>
        
        {/* Main Navigation Tabs */}
        {!['editor', 'session', 'vt-editor', 'vt-session'].includes(view) && (
          <div className="training-tabs">
            <button
              className={view === 'exercises' ? 'active' : ''}
              onClick={() => setView('exercises')}
            >
              💪 {t('training.exercises.library')}
            </button>
            <button
              className={view === 'trainings' ? 'active' : ''}
              onClick={() => setView('trainings')}
            >
              🗓️ {t('training.virtualTrainings.library')}
            </button>
            <button
              className={view === 'history' ? 'active' : ''}
              onClick={() => setView('history')}
            >
              📊 {t('training.sessions.history')}
            </button>
            <button
              className={view === 'vt-history' ? 'active' : ''}
              onClick={() => setView('vt-history')}
            >
              📈 {t('training.virtualTrainings.history')}
            </button>
          </div>
        )}
      </div>

      {view === 'editor' && (
        <ExerciseEditor
          exercise={editingExercise}
          onSave={handleSaveExercise}
          onCancel={handleCancel}
          onDelete={handleDeleteExercise}
        />
      )}

      {view === 'session' && activeExercise && (
        <ExerciseSession
          exercise={activeExercise}
          onComplete={handleSessionComplete}
          onCancel={handleCancel}
        />
      )}

      {view === 'history' && (
        <ExerciseSessionHistory />
      )}

      {view === 'exercises' && (
        <ExerciseLibrary
          key={refreshKey}
          onCreateExercise={handleCreateExercise}
          onEditExercise={handleEditExercise}
          onStartSession={handleStartSession}
        />
      )}

      {/* Virtual Training Views */}
      {view === 'vt-editor' && (
        <VirtualTrainingEditor
          trainingId={editingTraining?.id}
          onSave={handleSaveTraining}
          onCancel={() => setView('trainings')}
          onDelete={handleDeleteTraining}
        />
      )}

      {view === 'vt-session' && activeTraining && (
        <VirtualTrainingSession
          training={activeTraining}
          onComplete={handleTrainingComplete}
          onCancel={handleTrainingCancel}
        />
      )}

      {view === 'vt-history' && (
        <VirtualTrainingHistory />
      )}

      {view === 'trainings' && (
        <div className="virtual-trainings-view">
          <div className="view-header">
            <h2>{t('training.virtualTrainings.library')}</h2>
            <button onClick={handleCreateTraining} className="btn-primary">
              ➕ {t('training.virtualTrainings.create')}
            </button>
          </div>
          <p className="placeholder">Virtual Trainings List Component wird noch implementiert...</p>
        </div>
      )}
    </div>
  );
};

export default Training;
